import { useEffect, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { Bold, Italic, Heading2, Heading3, List, ListOrdered, Quote, Link2, ImagePlus, Undo2, Redo2, Minus } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage, BACKEND_URL } from "../lib/api";

export function RichEditor({ value, onChange }) {
  const fileRef = useRef(null);
  const editor = useEditor({
    extensions: [StarterKit.configure({ heading: { levels: [2, 3] } }), Link.configure({ openOnClick: false }), Image],
    content: value || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: { attributes: { class: "tiptap prose-article", "data-testid": "rich-editor" } },
  });

  useEffect(() => {
    if (editor && value !== undefined && editor.getHTML() !== value && !editor.isFocused) editor.commands.setContent(value || "", false);
  }, [value, editor]);

  if (!editor) return null;

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd);
      editor.chain().focus().setImage({ src: `${BACKEND_URL}${data.url}` }).run();
      toast.success("Image inserted");
    } catch (err) { toast.error(errorMessage(err)); }
    e.target.value = "";
  };

  const setLink = () => {
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("Link URL", prev || "https://");
    if (url === null) return;
    if (url === "") return editor.chain().focus().unsetLink().run();
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const Btn = ({ onClick, active, label, children, testid }) => (
    <button type="button" data-testid={testid} onClick={onClick} title={label} className={`rounded-md p-2 transition-colors duration-150 ${active ? "bg-[var(--navy)] text-white" : "text-slate-600 hover:bg-slate-100"}`}>{children}</button>
  );

  return (
    <div className="overflow-hidden rounded-xl border border-slate-300 bg-white focus-within:border-[var(--teal)]">
      <div className="flex flex-wrap gap-0.5 border-b border-slate-200 bg-[var(--surface)] p-1.5">
        <Btn testid="editor-bold" label="Bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><Bold size={16} /></Btn>
        <Btn testid="editor-italic" label="Italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic size={16} /></Btn>
        <Btn testid="editor-h2" label="Heading 2" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 size={16} /></Btn>
        <Btn testid="editor-h3" label="Heading 3" active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><Heading3 size={16} /></Btn>
        <Btn testid="editor-bullets" label="Bullet list" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><List size={16} /></Btn>
        <Btn testid="editor-ordered" label="Numbered list" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered size={16} /></Btn>
        <Btn testid="editor-quote" label="Quote" active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()}><Quote size={16} /></Btn>
        <Btn testid="editor-hr" label="Divider" onClick={() => editor.chain().focus().setHorizontalRule().run()}><Minus size={16} /></Btn>
        <Btn testid="editor-link" label="Link" active={editor.isActive("link")} onClick={setLink}><Link2 size={16} /></Btn>
        <Btn testid="editor-image" label="Insert image" onClick={() => fileRef.current?.click()}><ImagePlus size={16} /></Btn>
        <span className="mx-1 w-px bg-slate-300" />
        <Btn testid="editor-undo" label="Undo" onClick={() => editor.chain().focus().undo().run()}><Undo2 size={16} /></Btn>
        <Btn testid="editor-redo" label="Redo" onClick={() => editor.chain().focus().redo().run()}><Redo2 size={16} /></Btn>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="editor-image-input" />
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
